import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
import {useToastContext} from '@github-ui/toast'
import {TagIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import type React from 'react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  graphql,
  type PreloadedQuery,
  readInlineData,
  useLazyLoadQuery,
  usePaginationFragment,
  usePreloadedQuery,
  useRelayEnvironment,
} from 'react-relay'

import {ERRORS, HOTKEYS, LABELS, VALUES} from '../constants'
import {commitUpdateIssueLabelsMutation} from '../mutations/update-issue-labels-mutation'
import {CompressedLabelsToken} from './CompressedLabelsToken'
import type {LabelPickerLabel$data, LabelPickerLabel$key} from './__generated__/LabelPickerLabel.graphql'
import type {LabelPickerLabelsPaginated$key} from './__generated__/LabelPickerLabelsPaginated.graphql'
import type {LabelPickerQuery} from './__generated__/LabelPickerQuery.graphql'
import type {LabelsListPaginationQuery} from './__generated__/LabelsListPaginationQuery.graphql'
import {type ExtendedItemProps, ItemPicker, type SharedBulkActionsItemPickerProps} from './ItemPicker'
import type {ItemGroup} from '../shared'
import {SharedEmptyPicker} from './SharedEmptyPicker'
import {commitSetLabelsForLabelableMutation} from '../mutations/set-labels-for-labelable-mutation'
import {commitUpdateIssueLabelsBulkMutation} from '../mutations/update-issue-labels-bulk-mutation'
import type {updateIssueLabelsBulkMutation$data} from '../mutations/__generated__/updateIssueLabelsBulkMutation.graphql'
import type {updateIssueLabelsBulkByQueryMutation$data} from '../mutations/__generated__/updateIssueLabelsBulkByQueryMutation.graphql'
import {commitUpdateIssueLabelsBulkByQueryMutation} from '../mutations/update-issue-labels-bulk-by-query-mutation'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'

type LabelPickerBaseProps = {
  labelableId?: string
  readonly: boolean
  labels: LabelPickerLabel$data[]
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  onIssueUpdate?: () => void
  /**
   * Whether to render the label picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  initialFilter?: string
  shortcutHintVisible?: boolean
}

type LabelPickerProps = LabelPickerBaseProps & {
  labelQueryRef: PreloadedQuery<LabelPickerQuery>
}

type LazyLabelPickerProps = LabelPickerBaseProps & {
  labelableTypeName: 'Issue' | 'PullRequest'
  repo: string
  owner: string
}

type BulkLabelPickerProps = Omit<LabelPickerProps, 'issueId' | 'labels'> & {
  existingIssueLabels: Map<string, string[]>
  labelAppliedToAll: LabelPickerLabel$data[]
  labelAppliedToSome: LabelPickerLabel$data[]
  connectionIds: {[key: string]: string[]}
} & SharedBulkActionsItemPickerProps

type LabelPickerInternalProps = Omit<LabelPickerProps, 'labelQueryRef'> & {
  labelQueryRef: LabelPickerLabelsPaginated$key
  onSelectionChange: (labels: LabelPickerLabel$data[]) => void
}

export const LabelPickerGraphqlQuery = graphql`
  query LabelPickerQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      ...LabelPickerLabelsPaginated
    }
  }
`

export const AssignedLabelFragment = graphql`
  fragment LabelPickerAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 20})
  @refetchable(queryName: "LabelPickerAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor) @connection(key: "LabelPicker_labels") {
      edges {
        node {
          ...LabelPickerLabel
        }
      }
    }
  }
`

export const LabelFragment = graphql`
  fragment LabelPickerLabel on Label @inline {
    id
    color
    name
    nameHTML
    description
    # eslint-disable-next-line relay/unused-fields
    url
  }
`

const CompressedLabelAnchor = ({
  size = 'small',
  labels,
  anchorProps,
  displayHotkey,
  MAX_DISPLAYED_LABELS = 2,
  readonly,
}: {
  size?: 'small' | 'large'
  labels: LabelPickerLabel$data[]
  anchorProps?: React.HTMLAttributes<HTMLElement>
  displayHotkey: boolean
  MAX_DISPLAYED_LABELS?: number
  readonly?: boolean
}) => {
  return labels?.length === 0 ? (
    <SharedEmptyPicker
      leadingIcon={TagIcon}
      hotKey={!readonly && displayHotkey ? HOTKEYS.labelPicker.toUpperCase() : undefined}
      label={LABELS.noLabels}
      anchorProps={anchorProps}
      readonly={readonly}
    />
  ) : (
    <CompressedLabelsToken
      size={size}
      labelNamesWithColor={labels.map(l => ({nameHTML: l.nameHTML, color: l.color}))}
      anchorProps={anchorProps}
      MAX_DISPLAYED_LABELS={MAX_DISPLAYED_LABELS}
      readonly={readonly}
    />
  )
}

const labelsGroup: ItemGroup = {groupId: 'labels'}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}

export function LabelPicker({labelableId, onIssueUpdate, ...props}: LabelPickerProps) {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      commitUpdateIssueLabelsMutation({
        environment,
        input: {issueId: labelableId ?? '', labels: selectedLabels},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLabels,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, labelableId, onIssueUpdate],
  )

  return <LabelPickerBase labelableId={labelableId} onSelectionChange={onSelectionChange} {...props} />
}

export function BulkIssuesLabelPicker({
  issuesToActOn,
  existingIssueLabels,
  labelAppliedToAll,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Will be in use once SelectPanel support partial selection
  labelAppliedToSome,
  connectionIds,
  useQueryForAction,
  repositoryId,
  query,
  onCompleted,
  onError,
  ...props
}: BulkLabelPickerProps) {
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      const labelsToAdd = selectedLabels.filter(l => !labelAppliedToAll.some(la => la.name === l.name.toLowerCase()))
      const labelsToRemove = labelAppliedToAll.filter(la => !selectedLabels.some(l => la.name === l.name.toLowerCase()))
      const args = {
        applyLabelIds: labelsToAdd.map(l => l.id),
        removeLabelIds: labelsToRemove.map(l => l.id),
      }
      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueLabels,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueLabelsBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            ...args,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueLabelsBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueLabelsBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            ...args,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueLabelsBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [
      connectionIds,
      environment,
      existingIssueLabels,
      issuesToActOn,
      labelAppliedToAll,
      onCompleted,
      onError,
      query,
      repositoryId,
      useQueryForAction,
    ],
  )

  return <LabelPickerBase labels={labelAppliedToAll} onSelectionChange={onSelectionChange} {...props} />
}

export function LabelPickerBase({
  labelQueryRef,
  ...rest
}: LabelPickerProps & {onSelectionChange: (labels: LabelPickerLabel$data[]) => void}) {
  const preloadedData = usePreloadedQuery<LabelPickerQuery>(LabelPickerGraphqlQuery, labelQueryRef)

  return preloadedData.repository ? (
    <LabelPickerBaseInternal {...rest} labelQueryRef={preloadedData.repository} />
  ) : null
}

export const LabelPickerLabelsPaginatedFragment = graphql`
  fragment LabelPickerLabelsPaginated on Repository
  @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "LabelsListPaginationQuery") {
    labels(first: $count, after: $cursor, orderBy: {field: NAME, direction: ASC})
      @connection(key: "Repository_labels") {
      edges {
        node {
          ...LabelPickerLabel
        }
      }
      totalCount
    }
  }
`

/**
 * The LazyLabelPicker fetches its data via `useLazyLoadQuery` instead of `usePreloadedQuery`.
 * It also uses the more generic `setLabelsForLabelable` mutation, instead of
 * the `updateIssue` mutation. Ideally we can use the same mutation in both places,
 * but it's lower risk to keep them separate for now.
 */
export function LazyLabelPicker(props: LazyLabelPickerProps) {
  const [fetchKey, setFetchKey] = useState(0)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: 'Cannot edit labels right now',
    anchorElement: props.anchorElement,
  })

  return (
    <PreloadedQueryBoundary onRetry={() => setFetchKey(fetchKey + 1)} fallback={createFallbackComponent}>
      <LazyLabelPickerInternal {...props} fetchKey={fetchKey} />
    </PreloadedQueryBoundary>
  )
}

function LazyLabelPickerInternal({
  labelableId,
  repo,
  owner,
  fetchKey,
  labelableTypeName,
  onIssueUpdate,
  ...props
}: LazyLabelPickerProps & {fetchKey: number}) {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      commitSetLabelsForLabelableMutation({
        environment,
        input: {labelableId: labelableId ?? '', labels: selectedLabels, labelableTypeName},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLabels,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, labelableId, labelableTypeName, onIssueUpdate],
  )

  const labelData = useLazyLoadQuery<LabelPickerQuery>(
    LabelPickerGraphqlQuery,
    {
      repo,
      owner,
    },
    {fetchKey},
  )

  return labelData.repository ? (
    <LabelPickerBaseInternal
      labelableId={labelableId}
      labelQueryRef={labelData.repository}
      onSelectionChange={onSelectionChange}
      {...props}
    />
  ) : null
}

function LabelPickerBaseInternal({
  readonly,
  labels,
  onSelectionChange,
  insidePortal,
  shortcutEnabled,
  labelQueryRef,
  anchorElement,
  nested = false,
  triggerOpen = false,
  initialFilter,
  shortcutHintVisible = true,
}: LabelPickerInternalProps) {
  const [filter, setFilter] = useState('')
  const {data, loadNext, isLoadingNext, hasNext} = usePaginationFragment<
    LabelsListPaginationQuery,
    LabelPickerLabelsPaginated$key
  >(LabelPickerLabelsPaginatedFragment, labelQueryRef)
  const totalNumberOfLabels = data.labels?.totalCount || 0
  const fetchedLabels = (data.labels?.edges || []).flatMap(a =>
    // eslint-disable-next-line no-restricted-syntax
    a?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, a.node)] : [],
  )

  useEffect(() => {
    if (hasNext && !isLoadingNext && fetchedLabels.length < VALUES.labelsMaxPreloadCount) {
      loadNext(VALUES.labelsPageSize)
    }
  }, [loadNext, fetchedLabels, totalNumberOfLabels, hasNext, isLoadingNext])

  const items = useMemo(() => {
    const repositoryLabels = fetchedLabels.slice().sort((a, b) => a.name.localeCompare(b.name))
    const sortedSelectedLabels = labels.slice().sort((a, b) => a.name.localeCompare(b.name))
    const allLabels = sortedSelectedLabels.concat(repositoryLabels.filter(l => !labels.find(l2 => l2.id === l.id)))
    if (!filter) return allLabels
    return allLabels.filter(l => l.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fetchedLabels, filter, labels])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((label: LabelPickerLabel$data) => label.id, [])

  const convertToItemProps = useCallback(
    (label: LabelPickerLabel$data): ExtendedItemProps<LabelPickerLabel$data> => {
      return {
        id: label.id,
        description: label.description ?? undefined,
        descriptionVariant: 'block',
        children: <SafeHTMLText sx={{wordBreak: 'break-word'}} html={label.nameHTML as SafeHTMLString} />,
        source: label,
        groupId: labels.find(l => l.id === label.id) ? labelsGroup.groupId : suggestionsGroup.groupId,
        leadingVisual: () => (
          <Box
            sx={{
              bg: `#${label.color}`,
              borderColor: `#${label.color}`,
              width: 14,
              height: 14,
              borderRadius: 10,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          />
        ),
      }
    },
    [labels],
  )

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      return (
        <CompressedLabelAnchor
          labels={labels}
          readonly={readonly}
          displayHotkey={shortcutEnabled && shortcutHintVisible}
          anchorProps={readonly ? undefined : anchorProps}
        />
      )
    },
    [anchorElement, shortcutHintVisible, labels, readonly, shortcutEnabled],
  )

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (labels.length > 0) {
      itemGroups.push(labelsGroup)
    }
    if (items.length > 0 && items.length - labels.length > 0) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [labels, items])

  return (
    <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1}}>
      <ItemPicker
        items={items}
        initialSelectedItems={labels}
        openHotkey={shortcutEnabled ? HOTKEYS.labelPicker : undefined}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={`Filter labels ${
          fetchedLabels.length >= VALUES.labelsMaxPreloadCount ? `(${VALUES.labelsMaxPreloadCount} labels loaded)` : ''
        }`}
        selectionVariant="multiple"
        onSelectionChange={onSelectionChange}
        renderAnchor={renderAnchor}
        groups={groups}
        insidePortal={insidePortal}
        height={'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'Label results'}
        triggerOpen={triggerOpen}
        initialFilter={initialFilter}
      />
    </Box>
  )
}

try{ CompressedLabelAnchor.displayName ||= 'CompressedLabelAnchor' } catch {}
try{ LabelPicker.displayName ||= 'LabelPicker' } catch {}
try{ BulkIssuesLabelPicker.displayName ||= 'BulkIssuesLabelPicker' } catch {}
try{ LabelPickerBase.displayName ||= 'LabelPickerBase' } catch {}
try{ LazyLabelPicker.displayName ||= 'LazyLabelPicker' } catch {}
try{ LazyLabelPickerInternal.displayName ||= 'LazyLabelPickerInternal' } catch {}
try{ LabelPickerBaseInternal.displayName ||= 'LabelPickerBaseInternal' } catch {}